import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { type FC, useMemo } from 'react';

import SubmitButton from 'components/common/formikForm/SubmitButton';
import FormField from 'components/common/formikForm/formField';
import { ConsultantLicenseFields } from 'components/v2/consultantLicenseFields';

import useTranslate from 'hooks/useTranslate';
import type { AdminConsultant, EditConsultantFormValues } from 'types';
import { editConsultantValidationSchema } from 'utils/validations/adminConsultants';
import {
    ADMIN_CONSULTANT_ROLES,
    getAdminConsultantRoleLabelKey,
    toLicenseFormValues,
} from 'views/v2/adminPanel/helpers/adminConsultants';

interface EditConsultantDialogProps {
    consultant: AdminConsultant;
    onClose: () => void;
    onSubmit: (values: EditConsultantFormValues) => void | Promise<void>;
}

export const EditConsultantDialog: FC<EditConsultantDialogProps> = ({
    consultant,
    onClose,
    onSubmit,
}) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const validationSchema = useMemo(() => editConsultantValidationSchema(translate), [translate]);
    const roleOptions = useMemo(
        () =>
            ADMIN_CONSULTANT_ROLES.map((role) => ({
                label: translate(getAdminConsultantRoleLabelKey(role)),
                value: role,
            })),
        [translate]
    );
    const initialValues = useMemo<EditConsultantFormValues>(
        () => ({
            firstname: consultant.firstname,
            middlename: consultant.middlename ?? '',
            lastname: consultant.lastname,
            email: consultant.email,
            role: consultant.role,
            licenses: toLicenseFormValues(consultant.licenses),
        }),
        [consultant]
    );

    return (
        <Dialog
            open
            fullWidth
            maxWidth="md"
            fullScreen={fullScreen}
            scroll="paper"
            onClose={onClose}
        >
            <Formik<EditConsultantFormValues>
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
            >
                {({ isSubmitting }) => (
                    <Box
                        component={Form}
                        sx={{
                            display: 'flex',
                            flex: 1,
                            flexDirection: 'column',
                            minHeight: 0,
                        }}
                    >
                        <DialogTitle>
                            <Typography component="span" variant="h6" sx={{ fontWeight: 700 }}>
                                {translate('editConsultant')}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 0.5 }}
                            >
                                {translate('editConsultantDescription')}
                            </Typography>
                        </DialogTitle>
                        <DialogContent dividers sx={{ py: 2.5 }}>
                            <Stack spacing={2.5}>
                                <Grid container spacing={1.5}>
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        <FormField
                                            required
                                            name="firstname"
                                            label="firstName"
                                            disabled={isSubmitting}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        <FormField
                                            name="middlename"
                                            label="middleName"
                                            disabled={isSubmitting}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        <FormField
                                            required
                                            name="lastname"
                                            label="lastName"
                                            disabled={isSubmitting}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 8 }}>
                                        <FormField
                                            required
                                            type="email"
                                            name="email"
                                            label="email"
                                            disabled={isSubmitting}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        <FormField
                                            required
                                            type="select"
                                            name="role"
                                            label="role"
                                            options={roleOptions}
                                            disabled={isSubmitting}
                                        />
                                    </Grid>
                                </Grid>

                                <Divider />

                                <ConsultantLicenseFields />
                            </Stack>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                            <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
                                {translate('cancel')}
                            </Button>
                            <SubmitButton loading={isSubmitting}>saveChanges</SubmitButton>
                        </DialogActions>
                    </Box>
                )}
            </Formik>
        </Dialog>
    );
};

export default EditConsultantDialog;
